import React, { useCallback } from 'react';

import { featureToggleType } from 'config/featureToggles';
import { ResourceType } from 'services/resources';
import useResource from 'hooks/useResource';
import useAdminResource from '../hooks/useAdminResource';

import AdminLayout from 'layouts/Admin.layout';
import FormModal from 'components/FormModal';
import Table from 'components/Table';
import Button from 'components/Button';
import Search from 'components/Search';

import FeatureFlagForm from '../components/forms/FeatureFlag.form';
import { EditAction, DeleteAction } from '../components/TableActions';

import { mapLocationsToOptions } from 'helpers/mapToOptions';

const FeatureFlagsPage = () => {
  const locations = useResource(ResourceType.locations, { prefetch: true });
  const featureFlags = useAdminResource({
    resource: ResourceType.featureFlags,
    resourceName: 'feature flag',
    searchFields: [
      'config_name',
      'location_name',
      'attribute'
    ],
    transformFormData: (formData) => {
      if (formData.attribute === featureToggleType.DISABLED || formData.attribute === featureToggleType.VISIBLE) {
        formData = {
          ...formData,
          val: formData.val?.toString() || 'false'
        };
      }
      return formData;
    }
  });

  const onAction = useCallback((actionType, item) => {
    switch (actionType) {
      case EditAction.actionType:
        if (item.attribute === featureToggleType.DISABLED || item.attribute === featureToggleType.VISIBLE) {
          item = {
            ...item,
            val: item.val === 'true',
          };
        }
        featureFlags.onEdit(item);
        break

      case DeleteAction.actionType:
        featureFlags.onDelete(item.id);
        break;

      default: // make eslint happy :)
    }
  }, [featureFlags]);

  return (
    <AdminLayout
      title="Feature Flags"
      icon="check-square"
      extra={[
        <Search
          placeholder={featureFlags.strings.searchPlaceholder}
          value={featureFlags.searchVal}
          onChange={value => featureFlags.search(value)}
        />,
        <Button
          type="primary"
          icon="plus"
          onClick={() => {
            featureFlags.onCreate();
          }}
        >
          {featureFlags.strings.addButton}
        </Button>
      ]}
    >
      <FormModal
        title={featureFlags.strings.formTitle}
        form={featureFlags.form}
        submitText={featureFlags.strings.formSubmitButton}
        onClose={() => featureFlags.form.reset()}
        width="316px"
      >
        <FeatureFlagForm form={featureFlags.form} locationOptions={mapLocationsToOptions(locations.state.list)} />
      </FormModal>
      <Table
        loading={featureFlags.isLoading || locations.state.isLoading}
        dataSource={featureFlags.list}
        actionsProps={{
          resource: 'feature flag',
        }}
        actions={[
          EditAction,
          DeleteAction,
        ]}
        onAction={onAction}
        rowKey="id"
      >
        <Table.Column title="ID" dataIndex="id" />
        <Table.Column title="Name" dataIndex="config_name" />
        <Table.Column title="Location" dataIndex="location_id" render={locationId => {
          const location = locations.state.list.find(l => l.location_id === locationId);
          if (!location) return '-';
          else return location.location_name;
        }} />
        <Table.Column title="Attribute" dataIndex="attribute" />
        <Table.Column title="Value" dataIndex="val" render={(val, item) => {
          const { attribute } = item;
          if (attribute === featureToggleType.VARIANT) {
            return val;
          } else {
            return val === 'true' ? 'Yes' : 'No';
          }
        }} />
      </Table>
    </AdminLayout>
  );
};

export default FeatureFlagsPage;